import { useMemo } from 'react';

const NODE_W = 220;
const NODE_H = 72;
const H_SEP = 56;
const V_SEP = 120;

function buildTree(nodes) {
  const map = {};
  nodes.forEach(n => {
    map[n.node_id] = { ...n, children: [], x: 0, y: 0, subtreeWidth: 0 };
  });
  let root = null;
  Object.values(map).forEach(n => {
    if (n.parent_id && map[n.parent_id]) {
      map[n.parent_id].children.push(n);
    }
    if (!n.parent_id || !map[n.parent_id]) {
      if (!root || n.level < root.level) root = n;
    }
  });
  const sortChildren = (node) => {
    node.children.sort((a, b) => a.node_id.localeCompare(b.node_id, undefined, { numeric: true }));
    node.children.forEach(sortChildren);
  };
  if (root) sortChildren(root);
  return { root, map };
}

// Bottom-up subtree width + top-down positioning layout.
// Guarantees zero overlap: each subtree reserves its full horizontal footprint
// before siblings are placed, and parents are centered over their children.
function layoutTree(root) {
  if (!root) return;

  // Phase 1 (bottom-up): compute the minimum width each subtree needs
  function computeWidth(node) {
    if (node.children.length === 0) {
      node.subtreeWidth = NODE_W;
      return;
    }
    node.children.forEach(computeWidth);
    const childrenWidth = node.children.reduce((sum, c) => sum + c.subtreeWidth, 0)
      + H_SEP * (node.children.length - 1);
    node.subtreeWidth = Math.max(NODE_W, childrenWidth);
  }

  // Phase 2 (top-down): assign x, y within the allocated horizontal band
  function assignPositions(node, left, width) {
    node.y = node.level * (NODE_H + V_SEP);

    if (node.children.length === 0) {
      node.x = left + (width - NODE_W) / 2;
      return;
    }

    const childrenWidth = node.children.reduce((sum, c) => sum + c.subtreeWidth, 0)
      + H_SEP * (node.children.length - 1);
    let childLeft = left + (width - childrenWidth) / 2;

    node.children.forEach(child => {
      assignPositions(child, childLeft, child.subtreeWidth);
      childLeft += child.subtreeWidth + H_SEP;
    });

    // Center parent exactly over the range of its children
    const firstChildCenter = node.children[0].x + NODE_W / 2;
    const lastChildCenter = node.children[node.children.length - 1].x + NODE_W / 2;
    node.x = (firstChildCenter + lastChildCenter) / 2 - NODE_W / 2;
  }

  computeWidth(root);
  assignPositions(root, 0, root.subtreeWidth);

  // Normalize: ensure min x = 0
  let minX = Infinity;
  const collectMinX = (n) => { if (n.x < minX) minX = n.x; n.children.forEach(collectMinX); };
  collectMinX(root);
  if (minX > 0) {
    const shift = (n) => { n.x -= minX; n.children.forEach(shift); };
    shift(root);
  }
}

// Build connections: orthogonal with shared horizontal bus
// Each connection is tagged with parentId and optionally childId for filtering
function buildConnections(root) {
  const connections = [];
  const traverse = (node) => {
    if (node.children.length > 0) {
      const parentCx = node.x + NODE_W / 2;
      const parentBottom = node.y + NODE_H;
      const busY = parentBottom + V_SEP * 0.4;

      connections.push({ x1: parentCx, y1: parentBottom, x2: parentCx, y2: busY, type: 'parent-bus', parentId: node.node_id });

      if (node.children.length === 1) {
        const child = node.children[0];
        const childCx = child.x + NODE_W / 2;
        connections.push({ x1: childCx, y1: busY, x2: childCx, y2: child.y, type: 'bus-child', parentId: node.node_id, childId: child.node_id });
      } else {
        const childXs = node.children.map(c => c.x + NODE_W / 2);
        const minCx = Math.min(...childXs);
        const maxCx = Math.max(...childXs);
        connections.push({ x1: minCx, y1: busY, x2: maxCx, y2: busY, type: 'bus', parentId: node.node_id });
        node.children.forEach(child => {
          const cx = child.x + NODE_W / 2;
          connections.push({ x1: cx, y1: busY, x2: cx, y2: child.y, type: 'bus-child', parentId: node.node_id, childId: child.node_id });
        });
      }
    }
    node.children.forEach(traverse);
  };
  traverse(root);
  return connections;
}

// Compute level lines from positioned nodes
function buildLevelLines(nodeMap, maxX) {
  const levelNames = ['Nivel 0', 'Nivel 1', 'Nivel 2', 'Nivel 3', 'Nivel 4', 'Nivel 5', 'Nivel 6'];
  const levels = {};
  Object.values(nodeMap).forEach(n => {
    if (!levels[n.level]) levels[n.level] = [];
    levels[n.level].push(n);
  });
  const lines = [];
  Object.entries(levels).forEach(([lvl, nodes]) => {
    const y = nodes[0].y - 16;
    lines.push({ level: parseInt(lvl), y, label: levelNames[parseInt(lvl)] || `Nivel ${lvl}`, width: maxX + NODE_W + 80 });
  });
  return lines.sort((a, b) => a.level - b.level);
}

// Flat horizontal layout for a single-level filter
function layoutFlat(filteredNodes) {
  const sep = NODE_W + H_SEP;
  const positions = {};
  const sorted = [...filteredNodes].sort((a, b) => a.node_id.localeCompare(b.node_id, undefined, { numeric: true }));
  sorted.forEach((n, i) => {
    positions[n.node_id] = { x: i * sep, y: 40 };
  });
  const levelNames = ['Nivel 0', 'Nivel 1', 'Nivel 2', 'Nivel 3', 'Nivel 4', 'Nivel 5', 'Nivel 6'];
  const lvl = sorted[0]?.level ?? 0;
  const maxX = sorted.length > 0 ? (sorted.length - 1) * sep + NODE_W : NODE_W;
  const levelLines = [{ level: lvl, y: 24, label: levelNames[lvl] || `Nivel ${lvl}`, width: maxX + 80 }];
  return {
    positions,
    connections: [],
    levelLines,
    bounds: { width: maxX + 80, height: NODE_H + 120 },
    visibleNodeIds: new Set(sorted.map(n => n.node_id)),
  };
}

export function useOrgChartLayout(nodes, activeFilter) {
  return useMemo(() => {
    if (!nodes || nodes.length === 0) return { positions: {}, connections: [], levelLines: [], bounds: { width: 0, height: 0 }, visibleNodeIds: null };

    // Numeric level filter → flat single-row layout
    if (typeof activeFilter === 'number') {
      const filtered = nodes.filter(n => n.level === activeFilter);
      if (filtered.length === 0) return { positions: {}, connections: [], levelLines: [], bounds: { width: 0, height: 0 }, visibleNodeIds: new Set() };
      return layoutFlat(filtered);
    }

    // Full tree layout (no filter or 'vacant' filter)
    const { root, map } = buildTree(nodes);
    if (!root) return { positions: {}, connections: [], levelLines: [], bounds: { width: 0, height: 0 }, visibleNodeIds: null };

    // Subtree-width layout: zero overlap guaranteed
    layoutTree(root);

    // Collect final positions
    const positions = {};
    const collectPositions = (n) => {
      positions[n.node_id] = { x: n.x, y: n.y };
      n.children.forEach(collectPositions);
    };
    collectPositions(root);

    let maxX = 0, maxY = 0;
    Object.values(positions).forEach(p => {
      if (p.x + NODE_W > maxX) maxX = p.x + NODE_W;
      if (p.y + NODE_H > maxY) maxY = p.y + NODE_H;
    });

    const connections = buildConnections(root);
    const levelLines = buildLevelLines(map, maxX);

    return { positions, connections, levelLines, bounds: { width: maxX + 80, height: maxY + 80 }, visibleNodeIds: null };
  }, [nodes, activeFilter]);
}

export { NODE_W, NODE_H, V_SEP };
